import React, { useEffect, useRef, useState } from 'react';
import Icon from '@/components/Icon';
import Topbar from '@/layout/Topbar';
import { useShell } from '@/layout/AppShell';
import { ApiError, api } from '@/lib/api';
import { useCan } from '@/auth/AuthContext';
import { wiegandToBolid } from '@/lib/bolid';
import SkudPersonEditModal from '@/components/SkudPersonEditModal';

/**
 * СКУД → Люди.
 *
 * Админ-страница верхнего уровня (не в настройках). Показывает людей в
 * кэше СКУД, включая локально-созданные (`include_pending=1`, skud_id<0)
 * с плашкой «ожидает синхронизации» — чтобы админ видел, что ещё не
 * ушло в Orion.
 *
 * Создание: кнопка «+ Создать» открывает модалку. После сохранения
 * outbound-воркер синхронно (blocking=1) отправит в Orion `addperson`
 * и подменит отрицательный skud_id на реальный.
 *
 * Редактирование: клик по строке открывает модалку в режиме edit —
 * ФИО, подразделение, статус, плюс блок пропусков (SkudPassesBlock)
 * внутри той же модалки.
 *
 * Право: skud.persons/read для просмотра, skud.persons/write для действий.
 */
export default function SkudPersonsPage() {
  const { tweaks, updateTweak } = useShell();
  const can = useCan();
  const canRead  = can('skud.persons', 'read');
  const canWrite = can('skud.persons', 'write');

  const [rows, setRows]       = useState([]);
  const [total, setTotal]     = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  // Фильтры.
  const [q, setQ] = useState('');
  const [cardQ, setCardQ] = useState('');
  const [cardErr, setCardErr] = useState(null);
  const [role, setRole] = useState(''); // '' | 'student' | 'employee' | 'parent'
  const [showDismissed, setShowDismissed] = useState(false);
  const [showPending,   setShowPending]   = useState(true);

  // Поведение поля кода — как в модалке создания пропуска:
  //   • Enter конвертирует Wiegand → Bolid (короче 16 символов = Wiegand);
  //   • пока нет открытой модалки, печатные символы при фокусе ВНЕ полей
  //     ввода перехватываются в это поле — скан со считывателя попадает
  //     сюда целиком, а не в браузерный поиск (см. SkudPassEditModal).
  const cardInputRef = useRef(null);
  const convertCard = () => {
    setCardQ((prev) => {
      const raw = prev.trim();
      if (raw.length === 0 || raw.length >= 16) return prev;
      const r = wiegandToBolid(raw);
      if (!r.success) {
        setCardErr(r.error || 'Не удалось распознать код');
        return prev;
      }
      setCardErr(null);
      return r.code;
    });
  };

  // Пагинация.
  const [pageSize, setPageSize] = useState(50);
  const [offset,   setOffset]   = useState(0);

  // Модалка.
  const [modal, setModal] = useState(null); // null | { mode, person? }

  // Глобальный перехват сканера — только пока модалок нет: у модалки
  // создания пропуска свой такой же перехват, мешать ему нельзя.
  useEffect(() => {
    if (modal) return undefined;
    const onScanKey = (e) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const t = e.target;
      const tag = (t?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || t?.isContentEditable) return;
      if (e.key === 'Enter') {
        e.preventDefault();
        convertCard();
        cardInputRef.current?.focus();
        return;
      }
      if (e.key.length !== 1) return; // служебные клавиши не трогаем
      e.preventDefault();
      setCardQ((prev) => (prev + e.key).toUpperCase());
      setCardErr(null);
      cardInputRef.current?.focus();
    };
    window.addEventListener('keydown', onScanKey);
    return () => window.removeEventListener('keydown', onScanKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modal]);

  const reload = async () => {
    if (!canRead) return;
    setLoading(true); setError(null);
    try {
      const qs = new URLSearchParams();
      if (q)    qs.set('q', q);
      if (cardQ.trim()) qs.set('card', cardQ.trim());
      if (role) qs.set('role', role);
      if (!showDismissed) qs.set('exclude_dismissed', '1');
      if (showPending)    qs.set('include_pending',   '1');
      qs.set('limit',  String(pageSize));
      qs.set('offset', String(offset));
      const data = await api.get('/api/skud/persons?' + qs.toString());
      setRows(data.rows || []);
      setTotal(data.total || 0);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Не удалось загрузить список');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  // Сбрасываем страницу на 0 при смене фильтров/pageSize (иначе можно оказаться
  // на странице, которой уже нет после отсева).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setOffset(0); }, [q, cardQ, role, showDismissed, showPending, pageSize]);

  // Первичная загрузка + при смене фильтров/страницы с дебаунсом.
  useEffect(() => {
    const t = setTimeout(reload, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, cardQ, role, showDismissed, showPending, pageSize, offset, canRead]);

  const fullName = (p) => [p.lastName, p.firstName, p.middleName].filter(Boolean).join(' ');

  const inputStyle = {
    height: 30, padding: '0 10px', border: '1px solid var(--border-strong)',
    borderRadius: 6, fontSize: 13, background: 'var(--panel)', color: 'var(--fg)',
  };

  return (
    <>
      <Topbar
        breadcrumb={['СКУД', 'Люди']}
        density={tweaks.density}
        onDensityChange={(d) => updateTweak('density', d)}
      />
      <div className="ref-page">
        {!canRead ? (
          <div style={{ padding: 32, color: 'var(--fg-muted)' }}>
            Нет доступа: нужно право «skud.persons/read».
          </div>
        ) : (
          <>
            <div className="ref-toolbar">
              <span className="ref-toolbar__title">
                Люди в СКУД {!loading && <span className="muted">· {total}</span>}
              </span>
              <input
                className="ref-toolbar__search"
                placeholder="Поиск: ФИО"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                style={{ ...inputStyle, marginLeft: 16, minWidth: 240 }}
              />
              <input
                ref={cardInputRef}
                className="ref-toolbar__search mono"
                placeholder="Код карты: скан или HEX + Enter"
                title="Приложите карту к считывателю или введите HEX-код Wiegand и нажмите Enter — развернётся в формат Bolid"
                value={cardQ}
                onChange={(e) => { setCardQ(e.target.value.toUpperCase()); setCardErr(null); }}
                onKeyDown={(e) => {
                  if (e.key !== 'Enter') return;
                  e.preventDefault();
                  convertCard();
                }}
                style={{
                  ...inputStyle, marginLeft: 8, minWidth: 230,
                  ...(cardErr ? { borderColor: 'var(--red-fg, #dc2626)' } : null),
                }}
              />
              {cardQ && (
                <button
                  type="button"
                  className="btn btn--sm"
                  style={{ marginLeft: 4 }}
                  title="Очистить код карты"
                  onClick={() => { setCardQ(''); setCardErr(null); }}
                >
                  <Icon name="x" size={11} />
                </button>
              )}
              <select
                value={role}
                onChange={(e) => setRole(e.target.value)}
                style={{ ...inputStyle, marginLeft: 8 }}
              >
                <option value="">Все подразделения</option>
                <option value="employee">Сотрудники</option>
                <option value="parent">Родители</option>
                <option value="student">Ученики</option>
              </select>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginLeft: 12, fontSize: 13, whiteSpace: 'nowrap' }}>
                <input type="checkbox" checked={showDismissed}
                       onChange={(e) => setShowDismissed(e.target.checked)} />
                <span>Уволенные</span>
              </label>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginLeft: 12, fontSize: 13, whiteSpace: 'nowrap' }}>
                <input type="checkbox" checked={showPending}
                       onChange={(e) => setShowPending(e.target.checked)} />
                <span>Ожидают синка</span>
              </label>
              <div style={{ flex: 1 }} />
              {canWrite && (
                <button className="btn btn--primary btn--sm"
                        onClick={() => setModal({ mode: 'create' })}>
                  <Icon name="plus" size={12} /> Создать
                </button>
              )}
            </div>

            {cardErr && <div className="sd-edit__error" style={{ margin: '8px 16px' }}>{cardErr}</div>}
            {error && <div className="sd-edit__error" style={{ margin: '8px 16px' }}>{error}</div>}
            {loading && !rows.length && <div className="ref-empty">Загрузка…</div>}

            {rows.length > 0 && (
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left' }}>ФИО</th>
                      <th style={{ textAlign: 'left' }}>Подразделение</th>
                      <th style={{ textAlign: 'left', width: 140 }}>Табельный</th>
                      <th style={{ textAlign: 'left', width: 110 }}>Статус</th>
                      <th style={{ textAlign: 'right', width: 160 }}>СКУД</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr
                        key={r.skudId}
                        onClick={() => canWrite && setModal({ mode: 'edit', person: r })}
                        style={{ cursor: canWrite ? 'pointer' : 'default' }}
                      >
                        <td>
                          {r.photoThumb && (
                            <img src={r.photoThumb} alt=""
                                 style={{ width: 22, height: 22, borderRadius: '50%',
                                          objectFit: 'cover', marginRight: 8, verticalAlign: 'middle' }} />
                          )}
                          <span style={r.isDismissed ? { color: 'var(--fg-muted)', textDecoration: 'line-through' } : undefined}>
                            {fullName(r) || '—'}
                          </span>
                        </td>
                        <td>{r.department || <span style={{ color: 'var(--fg-muted)' }}>—</span>}</td>
                        <td className="mono">{r.tabNumber || '—'}</td>
                        <td>
                          {r.isDismissed
                            ? <span style={{ color: 'var(--fg-muted)' }}>уволен</span>
                            : <span>активен</span>}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          {r.pendingSync ? (
                            <span style={{ color: 'var(--warn, #b45f00)', fontSize: 12 }}>
                              ожидает синка
                            </span>
                          ) : (
                            <span className="mono" style={{ color: 'var(--fg-muted)' }}>#{r.skudId}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {!loading && !error && rows.length === 0 && (
              <div className="ref-empty">Ничего не найдено.</div>
            )}

            {(total > 0 || rows.length > 0) && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 12,
                margin: '12px 16px', flexWrap: 'wrap', fontSize: 13,
              }}>
                <div style={{ color: 'var(--fg-muted)' }}>
                  {rows.length > 0
                    ? `${offset + 1}–${offset + rows.length} из ${total}`
                    : `0 из ${total}`}
                </div>
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
                  <span style={{ color: 'var(--fg-muted)' }}>По</span>
                  <select
                    value={pageSize}
                    onChange={(e) => setPageSize(Number(e.target.value))}
                    style={{ ...inputStyle, width: 70 }}
                  >
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    <option value={200}>200</option>
                  </select>
                  <button
                    type="button"
                    className="btn btn--sm"
                    disabled={offset === 0 || loading}
                    onClick={() => setOffset(Math.max(0, offset - pageSize))}
                  >
                    <Icon name="chevron-left" size={10} /> Назад
                  </button>
                  <button
                    type="button"
                    className="btn btn--sm"
                    disabled={offset + rows.length >= total || loading}
                    onClick={() => setOffset(offset + pageSize)}
                  >
                    Вперёд <Icon name="chevron" size={10} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {modal && (
        <SkudPersonEditModal
          mode={modal.mode}
          person={modal.person}
          role={role || undefined}
          onClose={() => setModal(null)}
          // Модалка сама решает, когда закрываться (после create она остаётся
          // открытой в edit-режиме, чтобы можно было тут же выдать пропуск).
          // Здесь просто обновляем список — новая запись сразу видна.
          onSaved={() => reload()}
        />
      )}
    </>
  );
}
